// 后台面板到服务端的长连接（原生 WebSocket，路径 /ws，带 role=admin）。
//
// 生命周期是**应用级**的：页面打开就连、一直保持，不随某个弹窗开关。
// 组件只负责声明自己关心哪些设备（watchDevices），以及注册状态变化回调。
//
// 服务端把 role=admin 的连接放进独立的面板连接池，不会混进按 deviceId 的设备池。
import {getFingerprint, getServerBase} from './methods'

// 服务端 60s 收不到 ping 就断开，这里 50s 发一次
const HEARTBEAT_INTERVAL = 50000
const RECONNECT_BASE_DELAY = 3000
const RECONNECT_MAX_DELAY = 30000

let ws = null
let heartbeatTimer = null
let reconnectTimer = null
let retries = 0
let manualClosed = false

// 当前订阅的设备。重连后要重发一次——服务端的订阅集合是挂在连接上的，连接一断就没了
let watchedIds = []

// 最近一次已知的在线状态，供组件在渲染时取初值（避免等下一次推送才有数据）
const deviceStatus = {}

// 状态变化的订阅者。用 Set 便于组件卸载时精确注销
const statusHandlers = new Set()

function isOpen() {
	return ws && ws.readyState === WebSocket.OPEN
}

function emit(deviceId, online) {
	deviceStatus[deviceId] = online
	for (const handler of statusHandlers) {
		try {
			handler(deviceId, online)
		} catch (e) {
			// 单个订阅者出错不能影响其它订阅者
			console.warn('[adminWs] status handler failed:', e && e.message)
		}
	}
}

function clearTimers() {
	if (heartbeatTimer) {
		clearInterval(heartbeatTimer)
		heartbeatTimer = null
	}
	if (reconnectTimer) {
		clearTimeout(reconnectTimer)
		reconnectTimer = null
	}
}

function scheduleReconnect() {
	if (manualClosed || reconnectTimer) {
		return
	}
	// 指数退避，封顶 30s，避免服务端不可用时疯狂重连
	const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, Math.min(retries, 4)), RECONNECT_MAX_DELAY)
	retries++
	reconnectTimer = setTimeout(() => {
		reconnectTimer = null
		connect()
	}, delay)
}

/**
 * 建立连接。幂等：已连接或正在连接时直接返回。
 * 未登录（本地没有 sessionId）时不连——鉴权必然失败，等登录成功后再调一次。
 */
async function connect() {
	if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
		return
	}
	const sessionId = localStorage.getItem('sessionId')
	if (!sessionId) {
		return
	}

	manualClosed = false

	try {
		const base = await getServerBase()
		if (!base) {
			scheduleReconnect()
			return
		}
		const fp = await getFingerprint()
		const wsBase = base.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:')
		const url = `${wsBase}/ws?role=admin&fp=${encodeURIComponent(fp)}&sessionId=${encodeURIComponent(sessionId)}`

		const socket = new WebSocket(url)
		ws = socket

		socket.onopen = () => {
			retries = 0
			// 重连后补发订阅：服务端的订阅集合随旧连接一起没了
			if (watchedIds.length > 0) {
				sendWatch()
			}
			heartbeatTimer = setInterval(() => {
				if (socket.readyState === WebSocket.OPEN) {
					socket.send(JSON.stringify({type: 'ping'}))
				}
			}, HEARTBEAT_INTERVAL)
		}

		socket.onmessage = (evt) => {
			let msg = null
			try {
				msg = JSON.parse(evt.data)
			} catch (e) {
				return
			}
			if (msg.type === 'deviceStatusSnapshot') {
				const map = msg.data || {}
				for (const deviceId of Object.keys(map)) {
					emit(deviceId, map[deviceId] === true)
				}
			} else if (msg.type === 'deviceStatus') {
				const data = msg.data || {}
				if (data.deviceId) {
					emit(data.deviceId, data.online === true)
				}
			}
		}

		socket.onclose = () => {
			clearTimers()
			ws = null
			if (!manualClosed) {
				scheduleReconnect()
			}
		}

		socket.onerror = () => {
			// onerror 之后一定跟着 onclose，重连统一在 onclose 里处理
		}
	} catch (e) {
		ws = null
		scheduleReconnect()
	}
}

function disconnect() {
	manualClosed = true
	clearTimers()
	watchedIds = []
	retries = 0
	if (ws) {
		try {
			ws.close()
		} catch (e) {
			// 连接可能已在关闭中，忽略
		}
		ws = null
	}
}

function sendWatch() {
	if (!isOpen()) {
		return
	}
	ws.send(JSON.stringify({type: 'watchDevices', deviceIds: watchedIds}))
}

/**
 * 声明当前关心哪些设备。服务端按这个集合过滤推送，并立即回一份状态快照。
 * 连接还没建立时先记下来，onopen 时会自动补发。
 */
function watchDevices(deviceIds) {
	watchedIds = (deviceIds || []).filter(Boolean)
	if (watchedIds.length > 0) {
		connect()
	}
	sendWatch()
}

/** 取消订阅（不断开连接） */
function unwatchAll() {
	watchedIds = []
	sendWatch()
}

/**
 * 注册状态变化回调，返回注销函数。
 * 组件卸载时务必调用返回值，否则回调会一直累积。
 */
function onDeviceStatus(handler) {
	statusHandlers.add(handler)
	return () => statusHandlers.delete(handler)
}

/** 读取最近一次已知状态，没有记录时返回 undefined */
function getKnownStatus(deviceId) {
	return deviceStatus[deviceId]
}

export default {
	connect,
	disconnect,
	watchDevices,
	unwatchAll,
	onDeviceStatus,
	getKnownStatus
}
